import { useTranslation } from 'react-i18next';

import { Combobox, type ComboboxOption } from '@/shared/ui';

import { parsePrincipal, type Principal, principalValue, type PrincipalType } from './principal';
import { ASSIGNABLE_ROLES, type CustomRole, type Team } from './types';

interface Props {
  type: PrincipalType;
  onTypeChange: (type: PrincipalType) => void;
  value: Principal | null;
  onChange: (principal: Principal | null) => void;
  teams: readonly Team[];
  customRoles: readonly CustomRole[];
  /** Hide the team type (e.g. where only workspace roles make sense). */
  allowTeams?: boolean;
  size?: 'sm' | 'md';
}

/**
 * Subject of a board access rule. Two separate choices so team and role principals are never
 * mixed in one ambiguous list: the TYPE (team / workspace role) and then the principal. The
 * workspace-role list holds the standard roles AND the custom roles (searchable, marked).
 * The role that is GRANTED is a different control (the six standard board roles only).
 */
export function PrincipalSelect({
  type,
  onTypeChange,
  value,
  onChange,
  teams,
  customRoles,
  allowTeams = true,
  size = 'sm',
}: Props) {
  const { t } = useTranslation();
  const options: ComboboxOption[] =
    type === 'team'
      ? teams.map((team) => ({ value: `team:${team.id}`, label: team.name }))
      : [
          ...ASSIGNABLE_ROLES.map((role) => ({
            value: `role:${role}`,
            label: t(`roles.${role}`),
            group: t('roles.groupStandard'),
          })),
          ...customRoles.map((role) => ({
            value: `custom:${role.id}`,
            label: role.name,
            group: t('roles.groupCustom'),
          })),
        ];
  return (
    <>
      {allowTeams && (
        <Combobox
          label={t('access.principal.type')}
          value={type}
          size={size}
          options={[
            { value: 'team', label: t('access.principal.team') },
            { value: 'workspace_role', label: t('access.principal.workspaceRole') },
          ]}
          onChange={(next) => {
            onTypeChange(next === 'team' ? 'team' : 'workspace_role');
            onChange(null);
          }}
        />
      )}
      <Combobox
        label={t('access.principal.who')}
        value={principalValue(value)}
        size={size}
        placeholder={
          type === 'team' ? t('access.principal.pickTeam') : t('access.principal.pickRole')
        }
        searchPlaceholder={t('common.search')}
        emptyText={t('common.noResults')}
        options={options}
        onChange={(next) => {
          onChange(parsePrincipal(next));
        }}
      />
    </>
  );
}
